import { Select, UnstyledButton } from '@mantine/core';
import {
  IconAlertOctagon,
  IconCalendarDue,
  IconChecklist,
  IconClipboardList,
  IconFlag,
  IconGauge,
  IconHandStop,
  IconHourglass,
  IconLayoutDashboard,
  IconShieldCheck,
  IconSignature,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../../format.js';
import { offsetLabel } from '../../../inputs/lt-utc.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatTile } from '../../../StatTile.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { useShell } from '../../../shell-context.js';
import table from '../../materials/Materials.module.css';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { LoadError, TableSkeleton } from '../../purchasing/table.js';
import master from '../master/RaMaster.module.css';
import { LevelBadge } from '../RiskParts.js';
import { RA_REGISTER_PATH } from '../register/RaRegisterScreen.js';
import { RA_VERIFICATION_PATH } from '../verification/RaVerificationScreen.js';
import { verificationTone } from '../verification/view.js';
import classes from './RaDashboard.module.css';
import type {
  RaAttentionAcknowledgement,
  RaAttentionOperation,
  RaAttentionReview,
  RaDashboard,
  RaDashboardData,
  RaDashboardSource,
} from './types.js';

/** Kütüphanenin yolu (ofiste gözden geçirmesi gelen sürüm). */
const RA_LIBRARY_PATH = '/compliance/risk-assessment/library';
/** Panodaki listelerin en çok satırı; fazlası "ve N daha". */
const LIST_LIMIT = 6;

export interface RaDashboardScreenProps {
  source: RaDashboardSource;
}

/**
 * Compliance ▸ Risk Assessment ▸ Dashboard (K7; ADR 0095; R23; FD §2 "fleet risk summary, pending
 * approvals, high risks, overdue actions, reviews due, vessel compliance"). Özet kartları (atanmış
 * güncel RA, okundu onayı bekleyen ve gecikmiş, gözden geçirmesi gelen, yetki bekleyen, açık
 * doğrulama, High/Critical kalan riskli iş, durdurulan iş, uyum), ofiste şirket kütüphanesi ve
 * gemi uyumu tablosu, dikkat listeleri. Gemide geminin kendisi; ofiste filo ya da seçilen gemi.
 * Tasarım yok; dil Vessel Risk Register.
 */
export function RaDashboardScreen({ source }: RaDashboardScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.${key}`, options);
  const shell = useShell();
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; view: RaDashboardData }
  >({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', view: await source.load(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);
  const view = state.kind === 'ready' ? state.view : null;
  return (
    <div className={purchasing.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconLayoutDashboard size={26} />}
      />
      <p className={master.note}>{source.scope === 'ship' ? k('shipNote') : k('officeNote')}</p>
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !view ? (
        <Panel flush aria-label={k('title')}>
          <TableSkeleton columns={6} label={k('title')} />
        </Panel>
      ) : (
        <RaDashboardView
          view={view}
          scope={source.scope}
          onVessel={setVesselId}
          onNavigate={(path) => shell?.navigate(path)}
        />
      )}
    </div>
  );
}

export interface RaDashboardViewProps {
  view: RaDashboardData;
  scope: 'ship' | 'web';
  onVessel?: (vesselId: string | null) => void;
  onNavigate: (path: string) => void;
}

/** Panonun görünümü: veri dışarıdan (test edilebilir). */
export function RaDashboardView({ view, scope, onVessel, onNavigate }: RaDashboardViewProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.${key}`, options);
  const { dashboard } = view;
  const totals = dashboard.totals;
  const fleet = scope === 'web' && view.vesselId === null;
  const register = (vesselId: string, versionId?: string) =>
    `${RA_REGISTER_PATH}?vessel=${vesselId}${versionId ? `&version=${versionId}` : ''}`;
  const verification = (item: RaAttentionOperation) =>
    `${RA_VERIFICATION_PATH}?vessel=${item.vesselId}&id=${item.id}`;
  return (
    <>
      {scope === 'web' && onVessel ? (
        <div className={master.toolbar} style={{ padding: 0 }}>
          <Select
            className={master.filter}
            label={k('vessel')}
            data={[
              { value: '', label: k('fleet') },
              ...view.vessels.map((v) => ({ value: v.id, label: v.name })),
            ]}
            value={view.vesselId ?? ''}
            allowDeselect={false}
            onChange={(value) => onVessel(value ? value : null)}
          />
        </div>
      ) : null}
      <div className={classes.tiles}>
        <StatTile
          icon={<IconShieldCheck size={22} />}
          label={k('tiles.assigned')}
          value={totals.register.assigned}
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconSignature size={22} />}
          label={
            totals.register.late > 0
              ? k('tiles.pendingLate', { late: totals.register.late })
              : k('tiles.pending')
          }
          value={totals.register.pending}
          tone={
            totals.register.late > 0
              ? 'danger'
              : totals.register.pending > 0
                ? 'warning'
                : 'default'
          }
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconCalendarDue size={22} />}
          label={k('tiles.reviews')}
          value={reviewCount(dashboard)}
          tone={
            reviewOverdue(dashboard) > 0
              ? 'danger'
              : reviewCount(dashboard) > 0
                ? 'warning'
                : 'default'
          }
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconHourglass size={22} />}
          label={k('tiles.awaiting')}
          value={totals.operations.awaiting}
          tone={totals.operations.awaiting > 0 ? 'warning' : 'default'}
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconChecklist size={22} />}
          label={k('tiles.open')}
          value={totals.operations.open}
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconAlertOctagon size={22} />}
          label={k('tiles.highRisk')}
          value={totals.operations.highRisk}
          tone={totals.operations.highRisk > 0 ? 'danger' : 'default'}
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconHandStop size={22} />}
          label={k('tiles.stopped')}
          value={totals.operations.stopped}
          tone={totals.operations.stopped > 0 ? 'danger' : 'default'}
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconFlag size={22} />}
          label={
            dashboard.findings.overdue > 0
              ? k('tiles.findingsOverdue', { overdue: dashboard.findings.overdue })
              : k('tiles.findings')
          }
          value={dashboard.findings.open}
          tone={
            dashboard.findings.overdue > 0
              ? 'danger'
              : dashboard.findings.open > 0
                ? 'warning'
                : 'default'
          }
          variant="plain"
          wrapLabel
        />
        <StatTile
          icon={<IconGauge size={22} />}
          label={k('tiles.compliance')}
          value={totals.compliance === null ? '—' : `${totals.compliance}%`}
          tone={totals.compliance !== null && totals.compliance < 100 ? 'warning' : 'default'}
          variant="plain"
          wrapLabel
        />
      </div>
      {dashboard.library ? (
        <Panel title={k('library.title')} icon={<IconClipboardList size={18} />} divider>
          <dl className={classes.facts}>
            {(
              [
                ['published', dashboard.library.published, null],
                [
                  'inReview',
                  dashboard.library.inReview,
                  dashboard.library.inReview > 0 ? 'warning' : null,
                ],
                [
                  'approved',
                  dashboard.library.approved,
                  dashboard.library.approved > 0 ? 'warning' : null,
                ],
                ['draft', dashboard.library.draft, null],
                [
                  'reviewDue',
                  dashboard.library.reviewDue,
                  dashboard.library.reviewDue > 0 ? 'warning' : null,
                ],
                [
                  'reviewOverdue',
                  dashboard.library.reviewOverdue,
                  dashboard.library.reviewOverdue > 0 ? 'danger' : null,
                ],
                ['highResidual', dashboard.library.highResidual, null],
                ['total', dashboard.library.total, null],
              ] as const
            ).map(([key, value, tone]) => (
              <div key={key}>
                <dt>{k(`library.${key}`)}</dt>
                <dd data-tone={tone ?? undefined}>{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      ) : null}
      {fleet && dashboard.vessels.length > 0 ? (
        <Panel flush title={k('vessels.title')} icon={<IconGauge size={18} />} divider>
          <section
            className={table.tableWrap}
            aria-label={k('vessels.title')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${purchasing.table}`}>
              <thead>
                <tr>
                  <th scope="col">{k('vessels.vessel')}</th>
                  <th scope="col">{k('vessels.assigned')}</th>
                  <th scope="col">{k('vessels.acknowledged')}</th>
                  <th scope="col">{k('vessels.pending')}</th>
                  <th scope="col">{k('vessels.reviewOverdue')}</th>
                  <th scope="col">{k('vessels.outdated')}</th>
                  <th scope="col">{k('vessels.open')}</th>
                  <th scope="col">{k('vessels.awaiting')}</th>
                  <th scope="col">{k('vessels.stopped')}</th>
                  <th scope="col">{k('vessels.compliance')}</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.vessels.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <button
                        type="button"
                        className={master.codeButton}
                        aria-label={k('vessels.select', { name: v.name })}
                        onClick={() => onVessel?.(v.id)}
                      >
                        {v.name}
                      </button>
                    </td>
                    <td className={classes.num}>{v.register.assigned}</td>
                    <td className={classes.num}>{v.register.acknowledged}</td>
                    <td className={classes.num}>
                      {v.register.pending}
                      {v.register.late > 0 ? (
                        <span className={master.sub}>
                          {k('vessels.late', { count: v.register.late })}
                        </span>
                      ) : null}
                    </td>
                    <td className={classes.num}>{v.register.reviewOverdue}</td>
                    <td className={classes.num}>{v.register.outdated}</td>
                    <td className={classes.num}>{v.operations.open}</td>
                    <td className={classes.num}>{v.operations.awaiting}</td>
                    <td className={classes.num}>{v.operations.stopped}</td>
                    <td className={classes.num}>
                      {v.compliance === null ? '—' : `${v.compliance}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </Panel>
      ) : null}
      <div className={classes.grid}>
        <AttentionPanel
          title={k('attention.authorizations')}
          icon={<IconHourglass size={18} />}
          empty={k('attention.noAuthorizations')}
          items={dashboard.attention.authorizations}
          render={(item) => (
            <OperationItem
              item={item}
              fleet={fleet}
              onOpen={() => onNavigate(verification(item))}
            />
          )}
        />
        <AttentionPanel
          title={k('attention.stopped')}
          icon={<IconHandStop size={18} />}
          empty={k('attention.noStopped')}
          items={dashboard.attention.stopped}
          render={(item) => (
            <OperationItem
              item={item}
              fleet={fleet}
              onOpen={() => onNavigate(verification(item))}
            />
          )}
        />
        <AttentionPanel
          title={k('attention.highRisk')}
          icon={<IconAlertOctagon size={18} />}
          empty={k('attention.noHighRisk')}
          items={dashboard.attention.highRisk}
          render={(item) => (
            <OperationItem
              item={item}
              fleet={fleet}
              onOpen={() => onNavigate(verification(item))}
            />
          )}
        />
        <AttentionPanel
          title={k('attention.acknowledgements')}
          icon={<IconSignature size={18} />}
          empty={k('attention.noAcknowledgements')}
          items={dashboard.attention.acknowledgements}
          render={(item) => (
            <AcknowledgementItem
              item={item}
              fleet={fleet}
              onOpen={() => onNavigate(register(item.vesselId, item.versionId))}
            />
          )}
        />
        <AttentionPanel
          title={k('attention.reviews')}
          icon={<IconCalendarDue size={18} />}
          empty={k('attention.noReviews')}
          items={dashboard.attention.reviews}
          render={(item) => (
            <ReviewItem
              item={item}
              onOpen={() =>
                onNavigate(
                  item.vesselId ? register(item.vesselId, item.versionId) : RA_LIBRARY_PATH,
                )
              }
            />
          )}
        />
        <Panel title={k('settings.title')} icon={<IconGauge size={18} />} divider>
          <p className={classes.itemSub}>
            {k('settings.text', {
              months: dashboard.settings.reviewMonths,
              due: dashboard.settings.reviewDueDays,
              hours: dashboard.settings.authorizationMaxHours,
              ack: dashboard.settings.acknowledgementDueDays,
            })}
          </p>
        </Panel>
      </div>
    </>
  );
}

function reviewCount(d: RaDashboard): number {
  return d.library
    ? d.library.reviewDue + d.library.reviewOverdue
    : d.totals.register.reviewDue + d.totals.register.reviewOverdue;
}

function reviewOverdue(d: RaDashboard): number {
  return d.library ? d.library.reviewOverdue : d.totals.register.reviewOverdue;
}

function AttentionPanel<T extends { versionId?: string; id?: string; vesselId: string | null }>({
  title,
  icon,
  empty,
  items,
  render,
}: {
  title: string;
  icon: ReactNode;
  empty: string;
  items: readonly T[];
  render: (item: T) => ReactNode;
}) {
  const { t } = useTranslation();
  const shown = items.slice(0, LIST_LIMIT);
  return (
    <Panel
      flush
      title={title}
      icon={icon}
      divider
      extra={
        items.length > 0 ? (
          <StatusBadge tone="neutral" withIcon={false}>
            {items.length}
          </StatusBadge>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <p className={classes.empty}>{empty}</p>
      ) : (
        <>
          <ul className={classes.list} aria-label={title}>
            {shown.map((item) => (
              <li key={`${item.vesselId ?? ''}:${item.id ?? item.versionId ?? ''}`}>
                {render(item)}
              </li>
            ))}
          </ul>
          {items.length > shown.length ? (
            <p className={classes.more}>
              {t('riskDashboard.attention.more', { count: items.length - shown.length })}
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

function OperationItem({
  item,
  fleet,
  onOpen,
}: {
  item: RaAttentionOperation;
  fleet: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.${key}`, options);
  const sub = [
    fleet ? item.vesselName : null,
    `${item.code} Rev. ${item.revision}`,
    item.plannedStart
      ? `${formatDateTime(item.plannedStart, item.tzOffsetMin)} (${offsetLabel(item.tzOffsetMin)})`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      <span className={classes.itemMain}>
        <UnstyledButton className={master.codeButton} onClick={onOpen}>
          {item.number} · {item.operation}
        </UnstyledButton>
        <span className={classes.itemSub}>{sub}</span>
        {item.lastStop ? (
          <span className={classes.itemSub}>
            {k('attention.stopReason', {
              reason: item.lastStop.reason ?? '—',
              name: item.lastStop.byName ?? '—',
              time: formatDateTime(item.lastStop.at, item.lastStop.tzOffsetMin),
            })}
          </span>
        ) : null}
      </span>
      <span className={classes.itemSide}>
        {item.residual ? (
          <LevelBadge level={item.residual.level} score={item.residual.score} />
        ) : null}
        {item.masterRequired && item.group === 'awaiting' ? (
          <StatusBadge tone="warning" withIcon={false}>
            {k('attention.master')}
          </StatusBadge>
        ) : null}
        <StatusBadge tone={verificationTone(item.status)} withIcon={false}>
          {t(`riskVerification.statuses.${item.status}`)}
        </StatusBadge>
      </span>
    </>
  );
}

function AcknowledgementItem({
  item,
  fleet,
  onOpen,
}: {
  item: RaAttentionAcknowledgement;
  fleet: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.${key}`, options);
  return (
    <>
      <span className={classes.itemMain}>
        <UnstyledButton className={master.codeButton} onClick={onOpen}>
          {item.code} · {item.title}
        </UnstyledButton>
        <span className={classes.itemSub}>
          {[
            fleet ? item.vesselName : null,
            `Rev. ${item.revision}`,
            k('attention.since', {
              day: formatDay(item.since),
              days: item.days,
            }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <span className={classes.itemSide}>
        <StatusBadge tone={item.late ? 'danger' : 'warning'} withIcon={false}>
          {item.late ? k('attention.late') : k('attention.awaiting')}
        </StatusBadge>
      </span>
    </>
  );
}

function ReviewItem({ item, onOpen }: { item: RaAttentionReview; onOpen: () => void }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.${key}`, options);
  return (
    <>
      <span className={classes.itemMain}>
        <UnstyledButton className={master.codeButton} onClick={onOpen}>
          {item.code} · {item.title}
        </UnstyledButton>
        <span className={classes.itemSub}>
          {[item.vesselName, `Rev. ${item.revision}`, formatDay(item.reviewDay)]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <span className={classes.itemSide}>
        <StatusBadge tone={item.review === 'overdue' ? 'danger' : 'warning'} withIcon={false}>
          {k(`review.${item.review}`)}
        </StatusBadge>
      </span>
    </>
  );
}

/** Panonun yolu (menü ve zil). */
export const RA_DASHBOARD_PATH = '/compliance/risk-assessment/dashboard';
